import React, { useState } from 'react';
import {AiOutlineFullscreen, AiOutlineFullscreenExit, AiOutlineSetting} from 'react-icons/ai';
import { IconHome, IconOrca, IconSave, IconTrash } from '../../../Customiz3r/components/ToolSelectorTop/Icons.jsx';
import { toast } from "react-toastify";
import { getFullscreenActivationContext, getFullscreenToggleContext } from "../../../../components/providers/FullScreenProvider.jsx";
import { useNavigate } from "react-router-dom";
import DarkModeToggle from "../../../../components/HomeNavbar/DarkMode/index.jsx";
import { getThemeContext } from "../../../../components/providers/ThemeProvider.jsx";
import {
    confirmDelete,
    showAlert,
    showConfirm,
    showErrorAlert
} from "../../../../components/HelperAlerts/HelpersAlerts.jsx";
import axios from "axios";
import CustomOrderRequestModal from "../CustomOrderRequestModal.jsx";
import Download3mfModal from "./Download3mfModal.jsx";
import { AlertCircle, Check, ClipboardCheck, Download, Ruler } from "lucide-react";
import Toggle from "../../../../components/ui/Toggle.jsx";
import { openModelInOrca } from "../../../../lib/orca.js";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import { canDownloadDesignFiles } from '../../../../lib/permissions.js';

function ToolsActionsGeneral({
    modelo3d,
    setModelo3d,
    onSaveForOrder,
    onSaveForDownload,
    onExit,
    onSave,
    saveStatus = 'saved',
    selectedType = '2D',
    autoSaveEnabled = false,
    onToggleAutoSave,
}) {
    const navigate = useNavigate();
    const theme = getThemeContext();
    const fullscreenActivationContext = getFullscreenActivationContext();
    const fullscreenToggleContext = getFullscreenToggleContext();
    const [requestKeyring, setRequestKeyring] = useState(null);
    const [preparingRequest, setPreparingRequest] = useState(false);
    const [download3mfModel, setDownload3mfModel] = useState(null);
    const [downloading3mf, setDownloading3mf] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [openingOrca, setOpeningOrca] = useState(false);
    const authContext = useAuthContext();
    const canDownload = canDownloadDesignFiles(authContext);
    const showManualSave = selectedType === '2D' && typeof onSave === 'function';
    const saveTooltip = saveStatus === 'saving'
        ? 'Guardando...'
        : saveStatus === 'dirty' || saveStatus === 'error'
            ? 'Guardar cambios'
            : 'Todo guardado';
    const saveBadge = saveStatus === 'dirty'
        ? <span className="h-2.5 w-2.5 rounded-full bg-warning shadow" aria-hidden="true" />
        : saveStatus === 'error'
            ? <AlertCircle className="h-3.5 w-3.5 text-error" aria-hidden="true" />
            : <Check className="h-3.5 w-3.5 text-success" aria-hidden="true" />;

    const handleManualSaveClick = () => {
        if (saveStatus !== 'saving') onSave?.();
    };

    // console.log('dataModelo3ddddd', dataModelo3d);
    const handleSalirCustomiz3r = async () => {
        // El flujo de salida (con opción de guardar) vive en Customizer; aquí
        // solo queda el fallback por si el componente se usa sin él.
        if (onExit) {
            onExit();
            return;
        }

        const isConfirmed = await confirmDelete({
            title: 'Salir de Customiz3r',
            text: '¿Seguro que deseas salir sin guardar los cambios?',
            theme: theme,
            confirmButtonText: 'Salir',
            cancelButtonText: 'Cancelar',
            icon: 'question',
        });

        if (isConfirmed) {
            navigate('/profile/general');
        }
    };

    // Por el id, no por la ruta: la regex de /customiz3r dejaba sin "Eliminar"
    // a cualquier producto con otra routeBase (p. ej. /lightbox).
    const isModelCreated = Boolean(modelo3d?.id);

    const handleEliminarModelo = async () => {
        const isConfirmed = await confirmDelete({
            title: 'Eliminar modelo',
            text: '¿Seguro que deseas eliminar este modelo?',
            theme: theme,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            icon: 'warning',
        });

        if (isConfirmed) {
            try {
                // Aquí podrías agregar una llamada a la API para eliminar el modelo
                await showAlert({
                    title: 'Modelo eliminado',
                    text: 'El modelo se ha eliminado exitosamente.',
                    icon: 'success',
                    theme: theme
                });
                // Redirigir a la página de perfil
                navigate('/profile/general');
            } catch (error) {
                console.error('Error al eliminar el modelo:', error);
                await showErrorAlert({
                    text: 'Hubo un error al intentar eliminar el modelo.',
                    theme: theme
                });
            }
        }
    }


    const handleRequestOrder = async () => {
        if (preparingRequest) return;

        setPreparingRequest(true);
        try {
            const savedModel = onSaveForOrder ? await onSaveForOrder() : modelo3d;
            if (!savedModel?.id) {
                await showErrorAlert({
                    text: 'Guarda el modelo antes de pedirlo.',
                    theme: theme,
                });
                return;
            }
            setRequestKeyring(savedModel);
        } catch (error) {
            console.error('Error al preparar solicitud:', error);
            await showErrorAlert({
                text: 'No se pudo guardar el modelo antes de pedirlo.',
                theme: theme,
            });
        } finally {
            setPreparingRequest(false);
        }
    };

    const getCurrentSavedModel = async () => {
        if (onSaveForDownload) return await onSaveForDownload();
        return modelo3d;
    };

    const handleDownload3mf = async () => {
        if (downloading3mf) return;

        setDownloading3mf(true);
        try {
            const savedModel = await getCurrentSavedModel();
            if (!savedModel?.id) {
                await showErrorAlert({
                    text: 'Guarda el modelo antes de descargar el 3MF.',
                    theme: theme,
                });
                return;
            }
            setDownload3mfModel(savedModel);
        } catch (error) {
            console.error('Error al preparar descarga 3MF:', error);
            await showErrorAlert({
                text: 'No se pudo guardar el modelo antes de descargarlo.',
                theme: theme,
            });
        } finally {
            setDownloading3mf(false);
        }
    };

    /**
     * Un propietario normal nunca tiene permiso sobre `/3mftoken/` (es el
     * proyecto Orca interno de administración, con presets de impresora/
     * material) — antes el botón llamaba a esa ruta igualmente y siempre
     * terminaba en "Hubo un error al descargar el archivo 3MF". El paquete
     * comercial (MVP-04B) tampoco es todavía un derecho de descarga: llega
     * con compra + entitlement en MVP-05. MVP-04C §1.3.
     */
    const handleDownload3mfInfoForOwner = async () => {
        await showAlert({
            title: 'Descarga todavía no disponible',
            text: 'El paquete técnico de este diseño puede estar preparado, pero la descarga del 3MF se habilita al comprar el diseño (próximamente). Puedes consultar el precio y el estado del producto digital desde tu perfil.',
            icon: 'info',
            theme: theme,
        });
    };

    /**
     * Descargar la plantilla de montaje en pared (ZIP con PDF A4 1:1 + SVG).
     *
     * Se pregunta si meter también el 3MF porque son dos momentos distintos:
     * quien va a taladrar ya tiene la pieza impresa y no quiere volver a
     * bajarse el modelo, y quien acaba de diseñar suele querer las dos cosas de
     * una vez. El backend responde con `Template-Includes-3mf` para poder
     * avisar si se pidió el 3MF y no se pudo generar, en lugar de entregar un
     * ZIP más ligero sin decir nada.
     */
    const handleDownloadTemplate = async () => {
        if (downloadingTemplate) return;

        const savedModel = await getCurrentSavedModel();
        if (!savedModel?.id) {
            await showErrorAlert({
                text: 'Guarda el diseño antes de generar la plantilla.',
                theme: theme,
            });
            return;
        }

        // El 3MF solo para quien puede descargarlo (C3D-27); el resto se lleva
        // la plantilla sin preguntar.
        const choice = await showConfirm({
            title: 'Plantilla de montaje',
            text: 'Contornos de cada pieza, puntos de taladro y línea de nivel, '
                + 'a tamaño real en hojas A4 (PDF) y en un solo archivo (SVG).',
            icon: 'question',
            theme: theme,
            showDenyButton: canDownload,
            showCancelButton: true,
            confirmButtonText: canDownload ? 'Plantilla + 3MF' : 'Descargar plantilla',
            denyButtonText: 'Solo la plantilla',
            cancelButtonText: 'Cancelar',
        });
        if (choice.isDismissed) return;

        setDownloadingTemplate(true);
        try {
            const include3mf = canDownload && choice.isConfirmed;
            const response = await axios.get(
                `/customized-keyrings/${savedModel.id}/mounting-template/`,
                { params: { include_3mf: include3mf ? 1 : 0 }, responseType: 'blob' },
            );

            const disposition = response.headers['content-disposition'];
            const safeName = (savedModel.name || 'diseno').replace(/[\\/:*?"<>|]/g, '_');
            let fileName = `${safeName}-plantilla.zip`;
            const fileNameMatch = disposition?.match(/filename="?([^"]+)"?/);
            if (fileNameMatch?.[1]) fileName = fileNameMatch[1];

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            const toastTheme = theme === 'dark' ? 'dark' : 'light';
            if (include3mf && response.headers['template-includes-3mf'] === '0') {
                toast.warning('Plantilla descargada, pero el 3MF no se pudo generar', { theme: toastTheme });
            } else {
                toast.success('Plantilla de montaje descargada', { theme: toastTheme });
            }
        } catch (error) {
            console.error('Error al descargar la plantilla:', error);
            await showErrorAlert({
                text: error.response?.status === 400
                    ? 'El diseño no tiene contorno todavía: no hay nada que replantear.'
                    : 'Hubo un error al generar la plantilla de montaje.',
                theme: theme,
            });
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const handleOpenOrca = async () => {
        if (openingOrca) return;

        setOpeningOrca(true);
        try {
            const savedModel = await getCurrentSavedModel();
            if (!savedModel?.id) {
                await showErrorAlert({
                    text: 'Guarda el modelo antes de abrirlo en Orca.',
                    theme: theme,
                });
                return;
            }

            await openModelInOrca(savedModel.id);
            toast.success('Enlace temporal enviado a Orca', { theme: theme === 'dark' ? 'dark' : 'light' });
        } catch (error) {
            console.error('Error al abrir 3MF en Orca:', error);
            await showErrorAlert({
                text: error.response?.status === 404
                    ? 'El archivo 3MF no se encontró en el servidor.'
                    : 'Hubo un error al abrir el modelo en Orca.',
                theme: theme,
            });
        } finally {
            setOpeningOrca(false);
        }
    };

    return (
        <>
            <div className="flex items-center gap-2">
                {showManualSave && (
                    <div className="tooltip tooltip-left" data-tip={saveTooltip}>
                        <button
                            type="button"
                            onClick={handleManualSaveClick}
                            disabled={saveStatus === 'saving'}
                            className={`btn btn-sm btn-secondary btn-circle relative grid place-content-center overflow-visible p-0 leading-none ${saveStatus === 'dirty' || saveStatus === 'error' ? 'ring-2 ring-warning' : ''}`}
                            aria-label={saveTooltip}
                        >
                            {saveStatus === 'saving'
                                ? <span className="loading loading-spinner loading-xs" />
                                : <IconSave className="w-5 h-5" />}
                            {saveStatus !== 'saving' && (
                                <span className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-base-100 shadow-sm ring-1 ring-base-300">
                                    {saveBadge}
                                </span>
                            )}
                        </button>
                    </div>
                )}
                <div className="dropdown dropdown-end">
                    {/* El tooltip va en un wrapper: combinar tooltip + btn en el mismo
                        elemento descoloca el icono (DaisyUI). */}
                    <div className="tooltip tooltip-left" data-tip="Opciones">
                        <label tabIndex="0" className="btn btn-sm btn-secondary btn-circle grid place-content-center p-0 leading-none">
                            <AiOutlineSetting size={20} className="block" />
                        </label>
                    </div>
                    {/* Acotado a la altura de la pantalla y con scroll propio: en un movil
                        apaisado caben unos 380 px, el menu es mas alto, y sin esto
                        los ultimos elementos (pantalla completa, salir) se salian por
                        abajo sin forma de llegar a ellos. `dvh` y no `vh` porque en
                        movil la barra del navegador cambia de alto al desplazarse. */}
                    <div tabIndex="0" className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-56 max-h-[calc(100dvh-5.5rem)] overflow-y-auto overscroll-contain flex-nowrap">
                    <ul className="menu">

                        {onSaveForOrder && (
                            <li>
                                <button
                                    className="flex items-center gap-2 p-2"
                                    onClick={handleRequestOrder}
                                    disabled={preparingRequest}
                                >
                                    {preparingRequest ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <ClipboardCheck className="w-5 h-5" />
                                    )}
                                    <span>Pedir este diseño</span>
                                </button>
                            </li>
                        )}
                        <li>
                            <button
                                className="flex items-center gap-2 p-2"
                                onClick={canDownload ? handleDownload3mf : handleDownload3mfInfoForOwner}
                                disabled={canDownload && downloading3mf}
                            >
                                {canDownload && downloading3mf ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <Download className="w-5 h-5" />
                                )}
                                <span>{canDownload ? 'Descargar 3MF (proyecto interno)' : 'Descargar 3MF'}</span>
                            </button>
                        </li>
                        <li>
                            <button
                                className="flex items-center gap-2 p-2"
                                onClick={handleDownloadTemplate}
                                disabled={downloadingTemplate}
                            >
                                {downloadingTemplate ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <Ruler className="w-5 h-5" />
                                )}
                                <span>Plantilla de montaje</span>
                            </button>
                        </li>
                        {/* Proyecto Orca de producción (presets A1 Mini/PLA): quien puede descargar (C3D-27). */}
                        {canDownload && (
                            <li>
                                <button
                                    className="flex items-center gap-2 p-2"
                                    onClick={handleOpenOrca}
                                    disabled={openingOrca}
                                >
                                    {openingOrca ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <IconOrca className="w-5 h-5" />
                                    )}
                                    <span>Abrir en Orca</span>
                                </button>
                            </li>
                        )}


                        {/* "Cambiar nombre" vivía aquí; ahora el nombre se edita
                            directamente en el título, al lado de este menú. */}
                        {
                            isModelCreated && (

                                <li>

                                    <button
                                        className="flex items-center gap-2 p-2 hover:bg-red-500"
                                        onClick={handleEliminarModelo}
                                    >
                                        <IconTrash className="w-5 h-5" />
                                        <span>Eliminar</span>
                                    </button>
                                </li>
                            )

                        }
                        <li>
                            <button
                                onClick={handleSalirCustomiz3r}
                                className="flex items-center gap-2 p-2"
                            >
                                <IconHome className="w-5 h-5" />
                                <span>Salir</span>
                            </button>
                        </li>
                    </ul>
                    <div className="divider my-2" />
                    {onToggleAutoSave && (
                        <div className="px-2 pb-2">
                            <Toggle
                                checked={autoSaveEnabled}
                                onChange={onToggleAutoSave}
                                label="Guardado automático"
                            />
                            <p className="mt-1 text-xs text-base-content/60">
                                {autoSaveEnabled
                                    ? 'Los cambios se guardan solos.'
                                    : 'Guarda tú con el botón o Ctrl+S.'}
                            </p>
                        </div>
                    )}
                    <div className="divider my-2" />
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            className="btn btn-circle btn-sm"
                            onClick={fullscreenToggleContext}
                        >
                            {fullscreenActivationContext ? (
                                <AiOutlineFullscreenExit size={20} />
                            ) : (
                                <AiOutlineFullscreen size={20} />
                            )}
                        </button>
                        <div className="flex justify-center items-center">
                            <DarkModeToggle />
                        </div>
                    </div>
                    </div>
                </div>
            </div>
            {requestKeyring && (
                <CustomOrderRequestModal
                    keyring={requestKeyring}
                    onClose={() => setRequestKeyring(null)}
                />
            )}
            {download3mfModel && (
                <Download3mfModal
                    keyring={download3mfModel}
                    onClose={() => setDownload3mfModel(null)}
                />
            )}
        </>
    );
}

export default ToolsActionsGeneral;
